import { useCallback, useEffect, useState } from "react";
import { Card, BlockStack, InlineStack, Text, Badge, Button, Spinner, Autocomplete } from "@shopify/polaris";
import { ClipboardIcon } from "@shopify/polaris-icons";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";

// The CLABE a payer sends money TO.
//
// Deliberately worded to be impossible to confuse with `customers.bank_clabe`,
// which is the opposite direction — their own account, where WE send deposit
// refunds. Mixing those two up means asking a customer to pay into their own
// account, or refunding them into ours.
//
// Beneficiary details come from the Fintoc entity behind the Cobranza account
// (`GET /v2/accounts/{id}` → entity.holder_name / holder_id). They are the
// values a payer's banking app shows when they register the CLABE, so they are
// fixed here rather than guessed per-customer.
const BENEFICIARY_NAME = "GOMOVE";
const BENEFICIARY_BANK = "Fintoc";

function formatClabe(clabe) {
  if (!clabe) return "";
  return clabe.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
}

// Ready to paste into WhatsApp.
//
// The CLABE goes out as 18 BARE DIGITS. On screen it is shown grouped for
// readability, but several Mexican banking apps reject a pasted CLABE that
// contains spaces — so the display format and the message format differ on
// purpose.
//
// Asterisks are WhatsApp bold. They survive a copy as plain text (the markers
// are stripped), so bolding the CLABE makes it stand out without polluting
// what the customer actually pastes into their bank.
// `sharedBy` is set when the company pays through another company's CLABE
// (companies.paid_by_company_id): the CLABE is then not "exclusiva" of it.
function paymentMessage(clabe, ownerName, sharedBy = null) {
  if (sharedBy) {
    return [
      "*Datos para tu pago*",
      "",
      `Nombre: *${BENEFICIARY_NAME}*`,
      `Banco: ${BENEFICIARY_BANK}`,
      `CLABE: *${clabe}*`,
      "",
      `Esta CLABE es exclusiva de ${sharedBy} y sus razones sociales, incluida ${ownerName}. Cualquier transferencia que envíes aquí se aplica automáticamente a su cuenta.`,
    ].join("\n");
  }
  return [
    "*Datos para tu pago*",
    "",
    `Nombre: *${BENEFICIARY_NAME}*`,
    `Banco: ${BENEFICIARY_BANK}`,
    `CLABE: *${clabe}*`,
    "",
    ownerName
      ? `Esta CLABE es exclusiva de ${ownerName}. Cualquier transferencia que envíes aquí se aplica automáticamente a tu cuenta.`
      : "Esta CLABE es exclusiva tuya. Cualquier transferencia que envíes aquí se aplica automáticamente a tu cuenta.",
  ].join("\n");
}

function PaymentClabe({ ownerType, ownerId, ownerName, compact = false }) {
  const { hasPermission } = UserAuth();
  const [row, setRow] = useState(undefined);
  // Shared CLABE (companies only). `payer` is the company whose CLABE this one
  // uses; `covered` are the companies that use THIS one's CLABE.
  const [payer, setPayer] = useState(null);
  const [covered, setCovered] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  // DERIVED, not stored. Seeding state from a prop captures only the first
  // render, and these pages load the customer asynchronously: the prop arrives
  // empty, the effect starts fetching, then the prop changes, the effect
  // re-runs and its cleanup cancels that fetch — leaving the stored name null
  // forever, which is why the message showed no customer.
  const [fetchedName, setFetchedName] = useState(null);
  const [copied, setCopied] = useState(null); // 'clabe' | 'message'
  const displayName = (ownerName && ownerName.trim()) || fetchedName || null;

  useEffect(() => {
    if (!ownerType || !ownerId) {
      setRow(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      // A company linked to another shows the CLABE it should be paid on: the
      // paying company's. Its own CLABE stays live, it is just not handed out.
      let clabeOwner = ownerId;
      let nextPayer = null;
      let nextCovered = [];
      if (ownerType === "company") {
        const { data: me } = await supabase
          .from("companies").select("paid_by_company_id").eq("id", ownerId).maybeSingle();
        if (me?.paid_by_company_id) {
          const { data: p } = await supabase
            .from("companies").select("id, name").eq("id", me.paid_by_company_id).maybeSingle();
          nextPayer = p || { id: me.paid_by_company_id, name: `Empresa #${me.paid_by_company_id}` };
          clabeOwner = nextPayer.id;
        } else {
          const { data: kids } = await supabase
            .from("companies").select("id, name").eq("paid_by_company_id", ownerId).order("name");
          nextCovered = kids || [];
        }
      }
      if (cancelled) return;
      setPayer(nextPayer);
      setCovered(nextCovered);

      const { data } = await supabase
        .from("payment_clabes")
        .select("clabe, status")
        .eq("owner_type", ownerType)
        .eq("owner_id", clabeOwner)
        .in("status", ["pending", "active"])
        .maybeSingle();
      if (!cancelled) setRow(data || null);

      // Fetched unconditionally: the caller may pass the name late, or not at
      // all, and the message must never read "exclusiva de undefined".
      if (!cancelled) {
        if (ownerType === "company") {
          const { data: c } = await supabase
            .from("companies").select("name").eq("id", ownerId).maybeSingle();
          if (!cancelled) setFetchedName(c?.name || null);
        } else {
          const { data: c } = await supabase
            .from("customers").select("first_name, last_name").eq("id", ownerId).maybeSingle();
          if (!cancelled) {
            setFetchedName(c ? `${c.first_name || ""} ${c.last_name || ""}`.trim() || null : null);
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ownerType, ownerId, reloadKey]);

  const canLink = !compact && ownerType === "company" && hasPermission?.("payments.shared_clabe");
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const copy = async (what, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      /* clipboard unavailable (http, permissions) — the data is on screen */
    }
  };

  if (row === undefined) return compact ? null : <Spinner size="small" />;

  if (!row) {
    return wrap(
      <Text tone="subdued" variant="bodySm">
        Aún no tiene CLABE asignada. Se crea sola al registrar una reservación.
      </Text>
    );
  }

  if (row.status !== "active" || !row.clabe) {
    return wrap(<Badge tone="attention" progress="incomplete">Generando…</Badge>);
  }

  // Only the CLABE varies. Nombre (GOMOVE) and Banco (Fintoc) are identical on
  // every customer in the system, so rendering them per-profile is repetition
  // — they live in the copied message, which is where the customer needs them.
  // One line instead of a five-row card.
  // Stacked, not side by side: this card sits in a one-third column, where a
  // grouped 18-digit number plus a button on one line wraps mid-number.
  //
  // The number itself is the copy control. A real <button> rather than a
  // clickable div, so it is reachable by keyboard and announced as an action;
  // the styles are reset so it still reads as the heading it looks like.
  const content = (
    <InlineStack align="space-between" blockAlign="center" gap={200}>
      <button
        type="button"
        onClick={() => copy("clabe", row.clabe)}
        title="Clic para copiar la CLABE"
        style={{
          all: "unset",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Text variant="headingMd" as="span" numeric>
          {formatClabe(row.clabe)}
        </Text>
        {copied === "clabe" && (
          <Text tone="success" variant="bodySm" as="span">
            Copiada
          </Text>
        )}
      </button>

      <Button
        variant="plain"
        icon={ClipboardIcon}
        onClick={() => copy("message", paymentMessage(row.clabe, displayName, payer?.name))}
      >
        {copied === "message" ? "Copiado" : "Mensaje"}
      </Button>
    </InlineStack>
  );

  return wrap(content);

  function wrap(inner) {
    const titled = (
      <BlockStack gap={200}>
        <Text variant="headingSm" as="h3">
          CLABE para recibir sus pagos
        </Text>
        {inner}
        {payer && (
          <Text tone="subdued" variant="bodySm">
            Es la CLABE de {payer.name}.
          </Text>
        )}
        {covered.length > 0 && (
          <Text tone="subdued" variant="bodySm">
            También la usan: {covered.map((c) => c.name).join(", ")}.
          </Text>
        )}
        {canLink && covered.length === 0 && (
          <SharedClabeLink companyId={ownerId} payer={payer} onChanged={reload} />
        )}
      </BlockStack>
    );
    return compact ? titled : <Card>{titled}</Card>;
  }
}

export default PaymentClabe;

// Point a company at another company's CLABE, or back to its own. Only through
// set_company_payer(), which checks the permission, refuses chains and logs it.
function SharedClabeLink({ companyId, payer, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!editing || query.trim().length < 2) {
      setOptions([]);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      // Only companies that pay for themselves can be a payer: no chains.
      const { data } = await supabase
        .from("companies")
        .select("id, name, rfc")
        .ilike("name", `%${query.trim()}%`)
        .is("paid_by_company_id", null)
        .neq("id", companyId)
        .order("name")
        .limit(10);
      if (!cancelled) {
        setOptions((data || []).map((c) => ({ value: String(c.id), label: c.rfc ? `${c.name} · ${c.rfc}` : c.name })));
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [editing, query, companyId]);

  const save = async (paidBy) => {
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.rpc("set_company_payer", {
      p_company_id: companyId,
      p_paid_by: paidBy,
    });
    setBusy(false);
    if (err) return setError(err.message);
    setEditing(false);
    setQuery("");
    onChanged();
  };

  if (payer) {
    return (
      <InlineStack>
        <Button variant="plain" onClick={() => save(null)} loading={busy}>
          Usar su propia CLABE
        </Button>
        {error && <Text tone="critical" variant="bodySm">{error}</Text>}
      </InlineStack>
    );
  }

  if (!editing) {
    return (
      <InlineStack>
        <Button variant="plain" onClick={() => setEditing(true)}>
          Usar la CLABE de otra empresa
        </Button>
      </InlineStack>
    );
  }

  return (
    <BlockStack gap={100}>
      <Autocomplete
        options={options}
        selected={[]}
        onSelect={([id]) => id && save(Number(id))}
        loading={busy}
        textField={
          <Autocomplete.TextField
            label="Empresa que paga"
            value={query}
            onChange={setQuery}
            placeholder="Buscar empresa"
            autoComplete="off"
            connectedRight={<Button onClick={() => setEditing(false)}>Cancelar</Button>}
          />
        }
      />
      {error && <Text tone="critical" variant="bodySm">{error}</Text>}
    </BlockStack>
  );
}
